import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";

const loader = new GLTFLoader();

/**
 * Gather depth at zero charge, as a fraction of the clip's deepest frame.
 * The pose only appears after POSE_DELAY, by which point charge is already
 * ~0.15, so the first visible frame sits at ~49% depth -- unmistakably a
 * crouch -- and loads linearly to 100% from there.
 */
const CROUCH_MIN = 0.4;

/**
 * The pounce wiggle: a real cat treads its haunches while it gathers, so the
 * frozen crouch gets a lateral shimmy that quickens and widens with charge.
 * The cats stand ~0.32 m at the withers, so 12 mm of sway at full charge is
 * about a paw's width -- it reads as intent, not dance.  Height and pitch are
 * deliberately untouched: the origin sits at the paws, so sinking or tilting
 * the root puts the cat through the floor, and the frozen land frame already
 * supplies the drop.
 */
const WIGGLE_HZ0 = 6;         // treading rate at zero charge
const WIGGLE_HZ1 = 13;        // and at full
const WIGGLE_AMP = 0.012;     // m of lateral sway at full charge
const WIGGLE_YAW = 0.05;      // rad of counter-yaw, so the nose stays aimed

/**
 * The cling pose: the jump clip frozen at its fullest upward reach (found by
 * _measureStretch, the same world-space trick the crouch uses), pitched
 * nose-up about the paws so the body lies along the wall.
 *
 * The pitch is 0.95 rad, not the wall's full quarter turn: the origin sits
 * at the paws, so the pitch swings the hindquarters below and behind it --
 * the cats run 0.45-0.66 m nose to tail with 0.23-0.35 m of that behind the
 * origin, and at 54 degrees the rear hangs ~0.2 m under the cling point,
 * which reads as dangling hind legs against a fence and turns into clipped
 * grass if the cat clings within that of the ground.  CLING_LIFT buys some
 * of it back, CLING_SNUG closes the gap the pitch opens between the paws
 * and the face (the nose plane is 0.19 m out flat, cos(54 deg) of it
 * pitched), and 54 degrees itself is the angle a cat actually holds on a
 * fence -- forequarters high, spine on the diagonal -- rather than the flat
 * vertical of a gecko.
 */
const CLING_PITCH = 0.95;     // rad of nose-up about the paws
const CLING_SNUG = 0.05;      // m toward the wall, closing the pitch gap
const CLING_LIFT = 0.08;      // m up, keeping the hind legs mostly clear
const CLING_EASE = 0.12;      // s to blend in -- an arrest, not a snap

/**
 * The meow cue: the chin comes up.
 *
 * No cat has a meow clip -- the shipped clip sets stop at swipe -- and the
 * two rig families could not share a jaw bone if one did (the clips own
 * bone transforms outright anyway; see `morph`).  What both rigs CAN share
 * is the trick cling already leans on: the root.  A real cat meowing rocks
 * its weight back a touch and lifts its chin, and a few degrees of nose-up
 * pitch about the root reads as exactly that from the game's camera.
 *
 * The pitch is small on purpose.  The origin sits at the paws (the wiggle
 * comment above explains why tilting the root is normally forbidden), so
 * nose-up pitch swings the hindquarters DOWN: at 0.11 rad the rear of the
 * longest cat (~0.3 m behind the origin) sinks ~3 cm.  MEOW_LIFT buys most
 * of that back the same way CLING_LIFT does, and the centimetre that
 * remains for half a second is lost in the grass -- where 0.3+ rad would
 * be a cat rearing out of the ground.
 *
 * Rise is quick and settle is slow because that is how the gesture goes:
 * the head tosses up with the first vowel and eases back down as the sound
 * empties.  Both ends ride a smoothstep -- a linear corner on a half-second
 * gesture is visible as a twitch.
 */
const MEOW_PITCH = 0.11;      // rad of nose-up about the paws
const MEOW_LIFT = 0.022;      // m up, covering most of the rear's sink
const MEOW_RISE = 0.09;       // s to toss the chin up
const MEOW_FALL = 0.30;       // s to settle back down

/**
 * One cat: its scene graph, its mixer, and a crossfade helper.
 *
 * Locomotion clips carry the ground speed they were authored at, so playback
 * rate is derived from how fast the cat is actually moving rather than tuned
 * by eye.  That is what stops the paws sliding when the character moves at a
 * speed the animator never picked.
 *
 * "pounce" is a pseudo-clip: no cat has one, and the two rig families could
 * not share it if one did.  It is the `land` crouch held frozen (timeScale 0)
 * at a frame seeked deeper as the charge rises, plus a haunch wiggle applied
 * to the root in update().
 */
export class Cat {
  /** @param {ArrayBuffer} bytes  the .glb, straight from loadBytes -- the
      loader hands GLTFLoader the buffer it fetched, one residency, no
      decode step here (the old base64/atob hop is §3.1's retired triple).
      @param {object} meta  the parsed .clips.json sidecar */
  static async load(bytes, meta) {
    const gltf = await loader.parseAsync(bytes, "");
    return new Cat(gltf, meta);
  }

  constructor(gltf, meta) {
    this.meta = meta;
    this.root = gltf.scene;
    this.root.traverse((o) => {
      if (o.isMesh) {
        o.castShadow = true;
        o.receiveShadow = true;
        o.frustumCulled = false;      // skinned bounds lag the pose
      }
    });

    this.mixer = new THREE.AnimationMixer(this.root);
    this.actions = {};
    this.clipSpeed = {};
    for (const clip of gltf.animations) {
      const info = meta.clips.find((c) => c.name === clip.name) || {};
      const action = this.mixer.clipAction(clip);
      action.loop = info.loop === false ? THREE.LoopOnce : THREE.LoopRepeat;
      action.clampWhenFinished = info.loop === false;
      this.actions[clip.name] = action;
      this.clipSpeed[clip.name] = info.ground_speed_mps || 0;
    }
    this.current = null;
    this._charge = 0;
    this._wigglePhase = 0;
    this._clingBlend = 0;
    this._meowAge = Infinity;   // s since meow() -- Infinity is "not meowing"
    this._meowHold = 0;         // s to hold the chin up before settling
    // YXZ so the cling pitch is applied in the yawed frame -- about the
    // cat's own flanks, whichever way it faces.  The default XYZ order
    // pitches about world X, which lays the cat sideways on a wall it
    // meets along Z.  Inert while rotation.x is 0, which is all the time
    // the cat is not clinging.
    this.root.rotation.order = "YXZ";
    /**
     * A CatMorph from the customizer, or null.  The clips own bone.position
     * and bone.quaternion outright, so proportions cannot be written once at
     * load and left alone -- the next mixer.update() undoes them.  update()
     * re-applies the morph immediately after the mixer instead, which is the
     * only point in the frame where the pose is final and nothing has read
     * the bone matrices yet.
     */
    this.morph = null;
    this._measureCrouch();
    this._measureStretch();
  }

  /**
   * Find the land clip's gather by playing it through the mixer once at load
   * and reading back the mean bone height in world space.
   *
   * The obvious shortcut -- argmin of the hip track's local Y -- is wrong for
   * the imported rigs: their armature carries a -90 deg X rotation, so the
   * torso track's local Y runs along the ground and its argmin lands at the
   * clip's ends, not its crouch.  Matching the track by bone name fails there
   * too: the toon's only root-named translation track is flat to 0.5 mm, and
   * the one that moves is torso_02.  World space needs no per-rig knowledge,
   * and puts the generated cats' deepest frame at t=0.102 (35-45 mm below
   * standing) and the toon cats' at t=0.034 (27 mm).
   *
   * What is kept is the recovery branch, deepest frame to stood-back-up, as
   * (time, depth) pairs: play("pounce", { charge }) maps charge to a depth
   * and inverts the branch, so the crouch loads linearly with charge instead
   * of with whatever easing the landing animator used.
   */
  _measureCrouch() {
    const action = this.actions.land;
    this._crouchT = [0, 0];
    this._crouchD = [1, 0];
    if (!action) return;                // no land clip: pounce never renders

    const dur = action.getClip().duration;
    const bones = [];
    this.root.traverse((o) => { if (o.isBone) bones.push(o); });

    const N = 24;
    const t = [], h = [];
    action.play();
    action.setEffectiveWeight(1);
    for (let i = 0; i <= N; i++) {
      action.time = (dur * i) / N;
      this.mixer.update(0);
      this.root.updateMatrixWorld(true);
      let sum = 0;
      for (const b of bones) sum += b.matrixWorld.elements[13];
      t.push(action.time);
      h.push(sum / bones.length);
    }
    action.stop();

    let iDeep = 0;
    for (let i = 1; i <= N; i++) if (h[i] < h[iDeep]) iDeep = i;
    let iTop = iDeep;
    for (let i = iDeep; i <= N; i++) if (h[i] > h[iTop]) iTop = i;

    const T = [], D = [];
    let floor = Infinity;    // running min: the inverse lookup needs descent
    for (let i = iDeep; i <= iTop; i++) {
      floor = Math.min(floor, h[iTop] - h[i]);
      T.push(t[i]);
      D.push(floor);
    }
    if (T.length < 2 || D[0] < 0.005) {
      // A land with under 5 mm of dip is not a crouch -- keep the measured
      // shape as fractions instead: deepest at 30% of the clip, stood back
      // up by 70%, which is where all five real clips put them.
      this._crouchT = [0.30 * dur, 0.70 * dur];
      this._crouchD = [1, 0];
    } else {
      this._crouchT = T;
      this._crouchD = D;
    }
  }

  /**
   * Find the jump clip's fullest upward reach, the frame the cling freezes.
   *
   * Same instrument as _measureCrouch and for the same reason: the two rig
   * families cannot share a per-bone answer (the imported armature's -90 deg
   * X rotation breaks any local-axis reading), but the frame where the mean
   * bone height peaks in WORLD space is the launch extension on every rig --
   * body long, forelegs reaching -- which pitched against a wall is a cat at
   * full stretch on its claws.
   */
  _measureStretch() {
    const action = this.actions.jump;
    this._stretchT = 0;
    if (!action) return;                // no jump clip: cling never renders

    const dur = action.getClip().duration;
    const bones = [];
    this.root.traverse((o) => { if (o.isBone) bones.push(o); });

    const N = 24;
    action.play();
    action.setEffectiveWeight(1);
    let bestT = 0, bestH = -Infinity;
    for (let i = 0; i <= N; i++) {
      action.time = (dur * i) / N;
      this.mixer.update(0);
      this.root.updateMatrixWorld(true);
      let sum = 0;
      for (const b of bones) sum += b.matrixWorld.elements[13];
      if (sum / bones.length > bestH) { bestH = sum / bones.length; bestT = action.time; }
    }
    action.stop();
    this._stretchT = bestT;
  }

  /** Clip time whose crouch depth matches a charge, off the measured branch. */
  _crouchTime(charge) {
    const T = this._crouchT, D = this._crouchD;
    const want = D[0] * (CROUCH_MIN + (1 - CROUCH_MIN) * charge);
    for (let i = 1; i < T.length; i++) {
      if (D[i] <= want) {
        const f = (D[i - 1] - want) / ((D[i - 1] - D[i]) || 1);
        return T[i - 1] + (T[i] - T[i - 1]) * f;
      }
    }
    return T[T.length - 1];
  }

  /**
   * "pounce" and "cling" are not keys in `actions`: they borrow the land and
   * jump actions.  Every name resolves through here, incoming and outgoing
   * alike, which is what lets pounce -> jump crossfade the frozen crouch out
   * like any other clip instead of snapping to it.
   */
  _actionFor(name) {
    if (name === "pounce") return this.actions.land;
    if (name === "cling") return this.actions.jump;
    return this.actions[name];
  }

  play(name, { fade = 0.15, rate = 1, restart = false, charge = 0 } = {}) {
    const next = this._actionFor(name);
    if (!next) return;
    const pounce = name === "pounce";
    const cling = name === "cling";
    // The pseudo-clips freeze the action; anything else writes the real
    // rate.  That unconditional write is also what un-freezes `land` and
    // `jump` for their next true outing after a freeze left timeScale at 0.
    next.timeScale = pounce || cling ? 0 : rate;
    if (pounce) this._charge = THREE.MathUtils.clamp(charge, 0, 1);
    // The frame a frozen name sits on: the crouch loads with charge, the
    // cling holds one measured stretch.
    const seekT = pounce ? this._crouchTime(this._charge)
                : cling ? this._stretchT : 0;

    if (this.current === name && !restart) {
      if (pounce || cling) next.time = seekT;
      return;
    }

    const prev = this._actionFor(this.current);
    if (prev === next) {
      // pounce/land and cling/jump each share one action, and an action
      // cannot crossfade against itself -- both directions are just a
      // retime of a clip that is already up at full weight.
      next.paused = false;              // a clamped LoopOnce clip stays paused
      next.time = seekT;
      this.current = name;
      return;
    }

    next.reset();
    next.enabled = true;
    next.setEffectiveWeight(1);
    if (pounce || cling) next.time = seekT;
    if (prev) next.crossFadeFrom(prev, fade, false);
    next.play();
    this.current = name;
  }

  /** Playback rate that makes the paws match a given ground speed. */
  rateFor(name, speed) {
    const authored = this.clipSpeed[name];
    if (!authored) return 1;
    return THREE.MathUtils.clamp(speed / authored, 0.4, 2.4);
  }

  /** Seconds left in a one-shot clip, or 0 if it is not the current one. */
  remaining(name) {
    const a = this.actions[name];
    if (!a || this.current !== name) return 0;
    return Math.max(0, a.getClip().duration - a.time);
  }

  /**
   * Lift the chin for roughly `dur` seconds -- the length meow.js says the
   * sound will take, so the gesture lands with the voice.  Pure
   * presentation: the player state machine never hears about it, and a meow
   * mid-walk or mid-sit simply rides on top of whatever the cat is doing.
   * Retriggering restarts the gesture, matching a re-pressed button.
   */
  meow(dur = 0.6) {
    this._meowAge = 0;
    // The settle overlaps the sound's own tail, so hold ends MEOW_FALL
    // early; the floor keeps a very short meow from being all transition.
    this._meowHold = Math.max(0.12, dur - MEOW_FALL);
  }

  update(dt) {
    this.mixer.update(dt);
    // The morph goes first and the cling second, and they do not contend:
    // the morph rewrites BONE transforms the mixer just overwrote, while the
    // cling below moves the ROOT object the bones hang from.  Only the morph
    // has an ordering requirement -- straight after the mixer, before
    // anything reads a bone matrix -- so it takes the slot it needs.
    if (this.morph) this.morph.postMixer();

    // The cling's root work rides the same contract as the wiggle below:
    // main.js writes position and rotation.y fresh every frame before this
    // runs, so these are pure offsets.  rotation.x is ours alone -- main
    // never touches it -- so it is written every frame, zero included, or
    // the last cling's pitch would survive into the walk that follows it.
    this._clingBlend = this.current === "cling"
      ? Math.min(1, this._clingBlend + dt / CLING_EASE) : 0;
    const k = this._clingBlend;
    // The meow's chin-lift: rise, hold, settle, smoothstepped (see the
    // MEOW_* block).  Same pure-offset contract as the cling and the
    // wiggle, and it simply SUMS with the cling's pitch -- a cat meowing
    // off a fence lifts its head a little further up the wall, which is
    // just what it should do.
    this._meowAge += dt;
    const a = this._meowAge;
    const raw = a < MEOW_RISE ? a / MEOW_RISE
      : a < this._meowHold ? 1
      : Math.max(0, 1 - (a - this._meowHold) / MEOW_FALL);
    const m = raw * raw * (3 - 2 * raw);
    this.root.rotation.x = -CLING_PITCH * k - MEOW_PITCH * m; // -x is nose-up
    if (m > 0) this.root.position.y += MEOW_LIFT * m;
    if (k > 0) {
      const yaw = this.root.rotation.y;
      this.root.position.x += Math.sin(yaw) * CLING_SNUG * k;
      this.root.position.z += Math.cos(yaw) * CLING_SNUG * k;
      this.root.position.y += CLING_LIFT * k;
    }
    if (this.current !== "pounce") {
      this._wigglePhase = 0;
      return;
    }
    // main.js writes root.position and rotation.y fresh every frame before
    // calling update, so these are pure offsets: nothing accumulates, and the
    // first frame after pounce ends carries none of it.  Phase integrates so
    // the frequency can rise with charge without the sway jumping.
    const c = this._charge;
    this._wigglePhase +=
      (WIGGLE_HZ0 + (WIGGLE_HZ1 - WIGGLE_HZ0) * c) * 2 * Math.PI * dt;
    const s = Math.sin(this._wigglePhase) * (0.25 + 0.75 * c);
    const yaw = this.root.rotation.y;
    this.root.position.x += Math.cos(yaw) * s * WIGGLE_AMP;
    this.root.position.z -= Math.sin(yaw) * s * WIGGLE_AMP;
    this.root.rotation.y = yaw - s * WIGGLE_YAW;
  }
}
